/**
 * Trip Templates Service
 * 
 * Handles all operations related to trip templates:
 * - Fetching templates from Firestore
 * - Creating trips from templates
 * - Caching templates for offline access
 * - Template filtering and sorting
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  setDoc,
  onSnapshot,
  type Unsubscribe,
  Timestamp,
  addDoc,
} from 'firebase/firestore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { db } from '@/lib/firebase';
import { isInternalDebugBuild } from '@/services/logger';
import { createTrip, type CreateTripData, type TripDestination } from './trips';
import { createItineraryItem, type ItineraryItem, type ItineraryItemCategory, type ItineraryItemNotes } from './itinerary';
import { createChecklist } from './checklists';
import type {
  TripTemplate,
  FirestoreTripTemplate,
  TemplateCustomization,
  TemplateFilter,
  TemplateSortBy,
  TemplateStatus,
  TravelStyle,
} from '@/types/tripTemplates';
import { MIN_TEMPLATE_ITINERARY_DAYS } from '@/types/tripTemplates';
import {
  getCountryCodeFromDestination,
  getDestinationCoordinates,
  buildContextualSearchString,
} from '@/utils/geocodeHelpers';
import { mapTemplateActivityLocationFields } from '@/utils/templateActivityLocationMapping';
import { resolveMarketplaceTemplateStatus } from '@/utils/templateMarketplaceStatus';
import { withTimeout } from '@/utils/withTimeout';
import { fetchAndCacheLandmarkImage, getDestinationSlug } from '@/utils/landmarkImages';
import { getCuratedDestinationImage } from '@/data/destinationImages';
import type { TemplateDay } from '@/types/tripTemplates';
import { functions, httpsCallable } from '@/lib/functions';

const TEMPLATES_COLLECTION = 'tripTemplates';

function logTripTemplatesVerbose(...args: unknown[]): void {
  if (!isInternalDebugBuild()) return;
  // eslint-disable-next-line no-console
  console.log(...args);
}
const CACHE_KEY = 'trip_templates_cache';
const CACHE_EXPIRY_KEY = 'trip_templates_cache_expiry';
const CACHE_VERSION_KEY = 'trip_templates_cache_version';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const TEMPLATE_STEP_TIMEOUT_MS = 20_000;
const TEMPLATE_VERSION_COLLECTION = 'templateMetadata';
const TEMPLATE_VERSION_DOC = 'tripTemplatesPublic';

type RecordTemplateUsageRequest = {
  templateId: string;
  tripId: string;
  source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown';
  topicTag?: string;
};

type RecordTemplateUsageResponse = {
  success: boolean;
};

const recordTemplateUsageCallable = httpsCallable<RecordTemplateUsageRequest, RecordTemplateUsageResponse>(
  functions,
  'recordTemplateUsage',
);

const getTemplateVersionRef = () => doc(db, TEMPLATE_VERSION_COLLECTION, TEMPLATE_VERSION_DOC);

const parseVersionToken = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (value instanceof Timestamp) {
    return String(value.toMillis());
  }
  if (value && typeof value === 'object' && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    try {
      return String((value as { toMillis: () => number }).toMillis());
    } catch {
      return null;
    }
  }
  return null;
};

const fetchTemplateVersionToken = async (): Promise<string | null> => {
  try {
    const snap = await getDoc(getTemplateVersionRef());
    if (!snap.exists()) {
      return null;
    }
    const data = snap.data() as Record<string, unknown>;
    return parseVersionToken(data.version) || parseVersionToken(data.updatedAt);
  } catch {
    return null;
  }
};

const normalizeTemplateKeyPart = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

// Some environments have accidentally-seeded duplicate templates (same content, different doc IDs).
// Deduping here keeps the UI stable while data is cleaned up.
const dedupeTemplates = (templates: TripTemplate[]): TripTemplate[] => {
  const pickBetter = (a: TripTemplate, b: TripTemplate) => {
    // Prefer featured, then higher usageCount, then newer, then higher rating.
    if (!!a.featured !== !!b.featured) return a.featured ? a : b;
    const au = typeof a.usageCount === 'number' ? a.usageCount : 0;
    const bu = typeof b.usageCount === 'number' ? b.usageCount : 0;
    if (au !== bu) return au > bu ? a : b;
    const at = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
    const bt = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
    if (at !== bt) return at > bt ? a : b;
    const ar = typeof a.rating === 'number' ? a.rating : 0;
    const br = typeof b.rating === 'number' ? b.rating : 0;
    if (ar !== br) return ar > br ? a : b;
    return a;
  };

  const byKey = new Map<string, TripTemplate>();
  for (const t of templates || []) {
    if (!t) continue;
    const key = [
      normalizeTemplateKeyPart(t.title),
      normalizeTemplateKeyPart(t.destination),
      normalizeTemplateKeyPart(t.duration),
      normalizeTemplateKeyPart(t.createdBy),
    ].join('|');
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, t);
    } else {
      byKey.set(key, pickBetter(existing, t));
    }
  }
  return Array.from(byKey.values());
};

const normalizeNoonLocal = (d: Date) => {
  const out = new Date(d);
  // Noon local avoids DST/midnight UTC edge cases when shifting by days.
  out.setHours(12, 0, 0, 0);
  return out;
};

const parseMultiCityLabel = (raw: string | undefined | null): Array<{ city: string; country?: string }> => {
  const text = (raw || '').trim();
  if (!text) return [];

  // Split on multi-destination separators that usually separate cities, but avoid splitting on comma,
  // since commas often separate city/country ("Tokyo, Japan").
  const parts = text
    .split(/\s*(?:&|\/| and | – | - )\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);

  const out: Array<{ city: string; country?: string }> = [];
  for (const p of parts) {
    const seg = p.trim();
    if (!seg) continue;
    const segParts = seg.split(',').map((s) => s.trim()).filter(Boolean);
    const city = segParts[0];
    const country = segParts[1];
    if (!city) continue;
    const key = city.toLowerCase();
    if (out.some((x) => x.city.toLowerCase() === key)) continue;
    out.push({ city, country });
  }
  return out;
};

/**
 * Tour template titles are often marketing copy like "Mexico 10-Day Culture & Coast".
 * The segment after the duration can include "&" (not multiple cities). Peel off the leading
 * place hint before multi-city parsing so we don't invent fake cities such as "Coast".
 */
const stripLeadingTourTitlePlaceSegment = (title: string): string => {
  const trimmed = title.trim();
  if (!trimmed) return trimmed;
  const m = trimmed.match(/^(.+?)\s+\d+\s*[-–]?\s*day\b/i);
  return (m ? m[1] : trimmed).trim();
};

const inferCityIndexForTemplateDay = (
  day: TemplateDay,
  candidateCities: Array<{ city: string; country?: string }>,
  fallbackIndex: number,
): number => {
  if (!candidateCities.length) return fallbackIndex;

  // Deterministic path: if the template day explicitly specifies a city, use it.
  if (day.city && day.city.trim()) {
    const explicit = day.city.trim().toLowerCase();
    const idx = candidateCities.findIndex((c) => c.city.toLowerCase() === explicit);
    if (idx >= 0) return idx;
    // If city wasn't in candidates, keep fallback (caller should ensure candidates include day cities).
    return fallbackIndex;
  }

  const haystack = [
    day.title,
    day.description,
    ...day.activities.map((a) => `${a.title} ${a.location || ''} ${a.address || ''}`),
  ]
    .join(' ')
    .toLowerCase();

  let best = fallbackIndex;
  let bestScore = -1;
  candidateCities.forEach((c, idx) => {
    const needle = c.city.toLowerCase();
    if (!needle) return;
    // Count occurrences as a simple confidence score.
    const matches = haystack.split(needle).length - 1;
    if (matches > bestScore) {
      bestScore = matches;
      best = idx;
    }
  });

  // If we didn't match anything, keep the previous city.
  if (bestScore <= 0) return fallbackIndex;
  return best;
};

const deriveDestinationsFromTemplateItinerary = (
  template: TripTemplate,
  customizationStartDate: Date,
  totalDays: number,
): { destinations: TripDestination[]; dayToCityIndex: Map<number, number> } | null => {
  // Candidate city list: prefer the template's destination field (e.g. "Mexico").
  // Do not merge in title-derived cities when destination is already set — titles like
  // "Mexico 10-Day Culture & Coast" parse into bogus segments ("Coast") and break geocoding.
  let candidates = parseMultiCityLabel(template.destination);
  if (candidates.length === 0) {
    candidates = parseMultiCityLabel(stripLeadingTourTitlePlaceSegment(template.title));
  }
  // Union in explicit per-day cities, if present (this makes the inference deterministic).
  for (const d of template.itinerary || []) {
    const city = (d as any).city ? String((d as any).city).trim() : '';
    if (!city) continue;
    const key = city.toLowerCase();
    if (candidates.some((c) => c.city.toLowerCase() === key)) continue;
    const country = (d as any).country ? String((d as any).country).trim() : undefined;
    candidates.push({ city, country });
  }
  if (candidates.length <= 1) return null;

  const daysSorted = [...template.itinerary].sort((a, b) => a.day - b.day);
  if (!daysSorted.length) return null;

  // Build day -> cityIndex mapping (carry forward previous city on ambiguous days).
  const byDay = new Map<number, TemplateDay>();
  for (const d of daysSorted) byDay.set(d.day, d);

  const dayToCityIndex = new Map<number, number>();
  let currentIndex = 0;
  for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
    const d = byDay.get(dayNum);
    if (d) {
      currentIndex = inferCityIndexForTemplateDay(d, candidates, currentIndex);
    }
    dayToCityIndex.set(dayNum, currentIndex);
  }

  // Merge non-contiguous segments for the same city into a single destination range.
  // Example: "Dubai (days 1-3) → Abu Dhabi (day 4) → Dubai (day 5)" results in:
  // - Dubai: days 1-5
  // - Abu Dhabi: day 4
  const rangeByCity = new Map<number, { minDay: number; maxDay: number }>();
  for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
    const idx = dayToCityIndex.get(dayNum);
    if (typeof idx !== 'number') continue;
    const existing = rangeByCity.get(idx);
    if (!existing) {
      rangeByCity.set(idx, { minDay: dayNum, maxDay: dayNum });
    } else {
      existing.minDay = Math.min(existing.minDay, dayNum);
      existing.maxDay = Math.max(existing.maxDay, dayNum);
    }
  }

  const orderedCityIndices = Array.from(rangeByCity.entries())
    .sort((a, b) => a[1].minDay - b[1].minDay)
    .map(([idx]) => idx);

  // Convert ranges into TripDestination date ranges.
  const startNoon = normalizeNoonLocal(customizationStartDate);
  const destinations: TripDestination[] = orderedCityIndices.map((cityIndex) => {
    const range = rangeByCity.get(cityIndex)!;
    const shiftedStart = new Date(startNoon);
    shiftedStart.setDate(shiftedStart.getDate() + (range.minDay - 1));
    const shiftedEnd = new Date(startNoon);
    shiftedEnd.setDate(shiftedEnd.getDate() + (range.maxDay - 1));
    const meta = candidates[cityIndex];
    return {
      city: meta?.city || `City ${cityIndex + 1}`,
      country: meta?.country,
      startDate: normalizeNoonLocal(shiftedStart),
      endDate: normalizeNoonLocal(shiftedEnd),
    };
  });

  return { destinations, dayToCityIndex };
};

/**
 * Convert Firestore template to app TripTemplate type
 */
const convertFirestoreTemplate = (
  id: string,
  data: FirestoreTripTemplate
): TripTemplate => {
  return {
    ...data,
    id,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
    destinations: data.destinations?.map((dest) => ({
      ...dest,
      startDate: dest.startDate.toDate(),
      endDate: dest.endDate.toDate(),
    })),
  };
};

export type FetchTripTemplatesOptions = {
  /** Skip per-template landmark/network backfill (large parallel fan-out). Use for home-screen widget. */
  skipImageBackfill?: boolean;
};

/**
 * Fetch all templates from Firestore with optional caching
 */
export const fetchTripTemplates = async (
  useCache: boolean = true,
  options?: FetchTripTemplatesOptions,
): Promise<TripTemplate[]> => {
  try {
    const serverVersion = await fetchTemplateVersionToken();

    // Check cache first
    if (useCache) {
      const cached = await getCachedTemplates(serverVersion);
      if (cached) {
        logTripTemplatesVerbose('[TripTemplates] Returning cached templates');
        const dedupedCached = dedupeTemplates(cached);
        if (options?.skipImageBackfill) {
          return dedupedCached;
        }
        const hydratedCache = await backfillTemplateImages(dedupedCached);
        // If we added images, refresh the cache
        if (hydratedCache !== cached) {
          await cacheTemplates(hydratedCache, serverVersion);
        }
        return hydratedCache;
      }
    }

    logTripTemplatesVerbose('[TripTemplates] Fetching templates from Firestore');
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const templatesQuery = query(
      templatesRef,
      orderBy('featured', 'desc'),
      orderBy('usageCount', 'desc')
    );

    const snapshot = await getDocs(templatesQuery);
    const templates: TripTemplate[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as FirestoreTripTemplate;
      templates.push(convertFirestoreTemplate(doc.id, data));
    });

    const deduped = dedupeTemplates(templates);

    const hydratedTemplates = options?.skipImageBackfill
      ? deduped
      : await backfillTemplateImages(deduped);

    // Cache the results
    await cacheTemplates(hydratedTemplates, serverVersion);

    return hydratedTemplates;
  } catch (error) {
    console.error('[TripTemplates] Error fetching templates:', error);
    
    // Try to return cached data on error
    const cached = await getCachedTemplates();
    if (cached) {
      logTripTemplatesVerbose('[TripTemplates] Returning stale cache due to error');
      return cached;
    }
    
    throw error;
  }
};

/**
 * Get a single template by ID
 */
export const getTemplateById = async (
  templateId: string
): Promise<TripTemplate | null> => {
  try {
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    const templateDoc = await getDoc(templateRef);

    if (!templateDoc.exists()) {
      console.warn('[TripTemplates] Template not found:', templateId);
      return null;
    }

    const data = templateDoc.data() as FirestoreTripTemplate;
    return convertFirestoreTemplate(templateDoc.id, data);
  } catch (error) {
    console.error('[TripTemplates] Error fetching template:', error);
    throw error;
  }
};

/**
 * Subscribe to templates with real-time updates
 */
export const subscribeToTemplates = (
  callback: (templates: TripTemplate[]) => void
): Unsubscribe => {
  let isClosed = false;
  const emit = async (invalidateCache: boolean): Promise<void> => {
    try {
      if (invalidateCache) {
        await clearTemplatesCache();
      }
      const templates = await fetchTripTemplates(!invalidateCache);
      if (!isClosed) {
        callback(templates);
      }
    } catch (error) {
      console.error('[TripTemplates] Failed to refresh templates:', error);
      if (!isClosed) {
        callback([]);
      }
    }
  };

  void emit(false);

  const versionRef = getTemplateVersionRef();
  const unsubscribeVersion = onSnapshot(
    versionRef,
    async (snapshot) => {
      if (isClosed) return;
      const raw = snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : undefined;
      const nextVersion = parseVersionToken(raw?.version) || parseVersionToken(raw?.updatedAt);
      const currentVersion = await AsyncStorage.getItem(CACHE_VERSION_KEY);
      if (nextVersion && currentVersion && nextVersion === currentVersion) {
        return;
      }
      void emit(Boolean(nextVersion && currentVersion && nextVersion !== currentVersion));
    },
    () => {
      // Version doc is optional; rely on cache TTL and manual refresh when unavailable.
    },
  );

  return () => {
    isClosed = true;
    try {
      unsubscribeVersion();
    } catch {
      // no-op
    }
  };
};

/**
 * Create a trip from a template with customization
 */
export const createTripFromTemplate = async (
  userId: string,
  templateId: string,
  customization: TemplateCustomization,
  tracking?: { source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown'; topicTag?: string },
): Promise<string> => {
  try {
    console.log('[TripTemplates] Creating trip from template:', templateId);

    // Fetch the template
    const template = await getTemplateById(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    // Calculate end date based on actual itinerary span (handles templates whose itinerary day numbers exceed the declared duration)
    const itineraryMaxDay = template.itinerary.reduce((max, day) => Math.max(max, day.day), template.duration);
    const totalDays = Math.max(template.duration, itineraryMaxDay);
    const endDate = new Date(customization.startDate);
    endDate.setDate(endDate.getDate() + totalDays - 1);

    // Prepare trip data
    const tripData: CreateTripData = {
      name: customization.name || template.title,
      startDate: customization.startDate,
      endDate,
      travelMode: 'mixed',
      travelingFrom: customization.travelingFrom,
      templateId,
      templateTitle: template.title,
      // Avoid blocking template creation on external image fetches.
      // `createTrip` will backfill landmark imagery asynchronously.
      skipLandmarkImage: true,
    };

    // Handle destinations: if the template has multi-city data, shift dates to the user's selected start.
    if (template.destinations && template.destinations.length > 0) {
      const normalizedStart = new Date(customization.startDate);
      normalizedStart.setHours(12, 0, 0, 0);

      const templateBaseStart = new Date(template.destinations[0].startDate);
      templateBaseStart.setHours(12, 0, 0, 0);

      const shiftedDestinations: TripDestination[] = template.destinations.map((dest) => {
        const destStart = new Date(dest.startDate);
        destStart.setHours(12, 0, 0, 0);
        const destEnd = new Date(dest.endDate);
        destEnd.setHours(12, 0, 0, 0);

        const startOffsetDays = Math.round((destStart.getTime() - templateBaseStart.getTime()) / MS_PER_DAY);
        const endOffsetDays = Math.round((destEnd.getTime() - templateBaseStart.getTime()) / MS_PER_DAY);

        const shiftedStart = new Date(normalizedStart);
        shiftedStart.setDate(shiftedStart.getDate() + startOffsetDays);
        const shiftedEnd = new Date(normalizedStart);
        shiftedEnd.setDate(shiftedEnd.getDate() + endOffsetDays);

        return {
          ...dest,
          startDate: shiftedStart,
          endDate: shiftedEnd,
        };
      });

      tripData.destinations = shiftedDestinations;
      const destinationList = shiftedDestinations.map((d) => d.city).filter(Boolean);
      tripData.destination = destinationList.length > 0 ? destinationList.join(', ') : template.destination;
    } else {
      // Template doesn't provide destinations[]; infer multi-city destination date ranges from itinerary day/city cues.
      // Example: a Tokyo–Seoul 10-day template where Seoul starts on day 5 → city2 startDate = startDate + 4 days.
      const inferred = deriveDestinationsFromTemplateItinerary(template, customization.startDate, totalDays);
      if (inferred?.destinations?.length) {
        tripData.destinations = inferred.destinations;
        const destinationList = inferred.destinations.map((d) => d.city).filter(Boolean);
        tripData.destination = destinationList.length > 0 ? destinationList.join(', ') : template.destination;
      } else {
        const trimmedDest = (template.destination || '').trim();
        if (trimmedDest) {
          tripData.destination = trimmedDest;
        } else {
          const fromTitle = parseMultiCityLabel(stripLeadingTourTitlePlaceSegment(template.title));
          tripData.destination = (fromTitle[0]?.city || '').trim() || template.title.trim();
        }
      }
    }

    // Create the trip
    const tripId = await createTrip(userId, tripData);

    // Create itinerary items from template
    await withTimeout(
      createItineraryFromTemplate(tripId, userId, template, customization.startDate),
      TEMPLATE_STEP_TIMEOUT_MS,
      'Template itinerary creation'
    );

    // Set budget if provided in customization or use template budget
    if (customization.budget || template.estimatedBudget) {
      const { updateTrip } = await import('./trips');
      try {
        await withTimeout(
          updateTrip(tripId, userId, {
            budget: customization.budget || 
              Math.round((template.estimatedBudget.min + template.estimatedBudget.max) / 2),
            baseCurrency: template.estimatedBudget.currency,
          }),
          TEMPLATE_STEP_TIMEOUT_MS,
          'Template budget update'
        );
      } catch (error) {
        console.warn('[TripTemplates] Budget update timed out or failed during template creation:', error);
      }
    }

    // Create initial checklist
    try {
      await withTimeout(
        createChecklist(tripId, userId, `${template.title} Checklist`),
        TEMPLATE_STEP_TIMEOUT_MS,
        'Template checklist creation'
      );
    } catch (error) {
      // Non-critical; do not block the newly-created trip from opening.
      console.warn('[TripTemplates] Checklist creation timed out or failed:', error);
    }

    // Fire-and-forget analytics/usage side effects so they never block UI flow.
    void withTimeout(
      recordTemplateUsage(templateId, tripId, tracking),
      TEMPLATE_STEP_TIMEOUT_MS,
      'Template usage record'
    ).catch((error) => {
      console.warn('[TripTemplates] Template usage record timed out or failed:', error);
    });
    void withTimeout(
      trackTemplateUsage(templateId, userId, tripId, customization, tracking),
      TEMPLATE_STEP_TIMEOUT_MS,
      'Template usage analytics'
    ).catch((error) => {
      console.warn('[TripTemplates] Template analytics timed out or failed:', error);
    });

    console.log('[TripTemplates] Successfully created trip from template');
    return tripId;
  } catch (error) {
    console.error('[TripTemplates] Error creating trip from template:', error);
    throw error;
  }
};

/**
 * Map template activity category to itinerary item category
 * Templates use more descriptive categories, but itinerary items use a simplified set
 */
const mapTemplateCategory = (templateCategory: import('@/types/tripTemplates').TemplateActivityCategory): ItineraryItemCategory => {
  switch (templateCategory) {
    case 'lodging':
    case 'hotel':
      return 'hotel';
    case 'flight':
      return 'flight';
    case 'restaurant':
    case 'shopping':
    case 'attraction':
    case 'activity':
      return 'activity';
    case 'transport':
    case 'other':
    default:
      return 'other';
  }
};

/**
 * Create itinerary items from template
 */
type TemplateItineraryItemInput = Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>;

/** Template flows only have free-form note text; `ItineraryItemNotes` requires metadata for sharing UX. */
function buildTemplateItemNotes(text: string | undefined, userId: string): ItineraryItemNotes | undefined {
  const t = text?.trim();
  if (!t) return undefined;
  return {
    text: t,
    isShared: true,
    updatedAt: new Date(),
    updatedBy: userId,
  };
}

const createItineraryFromTemplate = async (
  tripId: string,
  userId: string,
  template: TripTemplate,
  startDate: Date
): Promise<void> => {
  try {
    console.log('[TripTemplates] Creating itinerary from template');

    const normalizedStart = new Date(startDate);
    normalizedStart.setHours(12, 0, 0, 0);

    // Extract destination city and country for contextual address building.
    // For multi-city templates without explicit destinations[], we infer per-day cityIndex from itinerary.
    const itineraryMaxDay = template.itinerary.reduce((max, day) => Math.max(max, day.day), template.duration);
    const totalDays = Math.max(template.duration, itineraryMaxDay);
    const inferred = (!template.destinations || template.destinations.length === 0)
      ? deriveDestinationsFromTemplateItinerary(template, startDate, totalDays)
      : null;

    const destinationParts = template.destination.split(',').map(s => s.trim());
    const destinationCity = destinationParts[0];
    const destinationCountry = destinationParts[1];

    const creationErrors: Array<{ day: number; activity: string; error: unknown }> = [];

    for (const day of template.itinerary) {
      // Calculate the actual date for this day
      const dayDate = new Date(normalizedStart);
      dayDate.setDate(normalizedStart.getDate() + (day.day - 1));

      const dayCityIndex = inferred?.dayToCityIndex?.get(day.day) ?? null;
      const dayCity =
        typeof dayCityIndex === 'number'
          ? inferred?.destinations?.[dayCityIndex]?.city
          : undefined;
      const dayCountry =
        typeof dayCityIndex === 'number'
          ? inferred?.destinations?.[dayCityIndex]?.country
          : undefined;

      for (const activity of day.activities) {
        const {
          geocodeHint: activityGeocodeHint,
          notesWithVenue,
        } = mapTemplateActivityLocationFields(
          activity,
          [dayCity, dayCountry].filter(Boolean).join(', ') || dayCity || destinationCity,
        );
        // Build a contextual address if the provided address is vague or missing
        let finalAddress = activity.address;
        
        // If no address or address is too short/vague, build a better one
        if (!finalAddress || finalAddress.length < 15) {
          finalAddress = buildContextualSearchString(
            activityGeocodeHint || activity.title,
            activity.address,
            dayCity || destinationCity,
            dayCountry || destinationCountry
          );
        }

        const combinedDescription =
          activity.notes && activity.notes.trim().length > 0
            ? [activity.description, activity.notes].filter(Boolean).join('\n')
            : activity.description;

        const itemData: TemplateItineraryItemInput = {
          title: activity.title,
          description: combinedDescription,
          category: mapTemplateCategory(activity.category),
          startDate: dayDate,
          startTime: activity.time,
          location: activityGeocodeHint,
          address: finalAddress,
          cityIndex: typeof dayCityIndex === 'number' ? dayCityIndex : null,
          source: 'template',
          status: 'planned',
          createdBy: userId,
          documents: [],
          notes: buildTemplateItemNotes(notesWithVenue, userId),
        };

        // Add end time if duration is specified
        if (activity.duration) {
          const [hours, minutes] = activity.time.split(':').map(Number);
          const endTime = new Date(dayDate);
          endTime.setHours(hours, minutes + activity.duration);
          itemData.endTime = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
        }

        try {
          await createItineraryItem(tripId, itemData);
        } catch (activityError) {
          creationErrors.push({ day: day.day, activity: activity.title, error: activityError });
          console.error('[TripTemplates] Failed to create itinerary item from template', {
            day: day.day,
            activity: activity.title,
            error: activityError,
          });

          // Fallback: try again with a minimal payload to avoid losing items when enrichment/validation fails
          try {
            await createItineraryItem(tripId, {
              title: activity.title,
              category: mapTemplateCategory(activity.category),
              startDate: dayDate,
              startTime: activity.time,
              source: 'template',
              status: 'planned',
              createdBy: userId,
              documents: [],
              description: combinedDescription,
              notes: buildTemplateItemNotes(notesWithVenue, userId),
              cityIndex: typeof dayCityIndex === 'number' ? dayCityIndex : null,
            });
            console.warn('[TripTemplates] Fallback creation succeeded for activity:', activity.title);
          } catch (fallbackError) {
            creationErrors.push({ day: day.day, activity: `${activity.title} (fallback)`, error: fallbackError });
            console.error('[TripTemplates] Fallback creation failed for activity:', {
              day: day.day,
              activity: activity.title,
              error: fallbackError,
            });
          }
        }
      }
    }

    if (creationErrors.length > 0) {
      console.warn('[TripTemplates] Completed with errors while creating itinerary items:', creationErrors.length, creationErrors);
    } else {
      console.log('[TripTemplates] Successfully created itinerary items');
    }
  } catch (error) {
    console.error('[TripTemplates] Error creating itinerary:', error);
    throw error;
  }
};

/**
 * Record template usage on the backend so Firestore rules keep template counters server-owned.
 */
const recordTemplateUsage = async (
  templateId: string,
  tripId: string,
  tracking?: { source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown'; topicTag?: string },
): Promise<void> => {
  try {
    await recordTemplateUsageCallable({
      templateId,
      tripId,
      source: tracking?.source,
      topicTag: tracking?.topicTag,
    });
  } catch (error) {
    console.error('[TripTemplates] Error recording usage count:', error);
    // Don't throw - this is analytics, shouldn't break the flow
  }
};

/**
 * Track template usage for analytics
 */
const trackTemplateUsage = async (
  templateId: string,
  userId: string,
  tripId: string,
  customization: TemplateCustomization,
  tracking?: { source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown'; topicTag?: string },
): Promise<void> => {
  try {
    // Import analytics service
    const { logAnalyticsEvent } = await import('./analyticsLogger');

    await logAnalyticsEvent('template_used', {
      templateId,
      tripId,
      hasCustomName: !!customization.name,
      hasCustomBudget: !!customization.budget,
      startDate: customization.startDate.toISOString(),
      source: tracking?.source,
      topicTag: tracking?.topicTag,
    });
  } catch (error) {
    console.error('[TripTemplates] Error tracking usage:', error);
    // Don't throw - analytics failure shouldn't break the flow
  }
};

export { resolveMarketplaceTemplateStatus } from '@/utils/templateMarketplaceStatus';

/**
 * Filter templates based on criteria
 */
export const filterTemplates = (
  templates: TripTemplate[],
  filter: TemplateFilter
): TripTemplate[] => {
  let filtered = [...templates];

  // Search query (title, destination, description)
  if (filter.searchQuery) {
    const query = filter.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (t) =>
        t.title.toLowerCase().includes(query) ||
        t.destination.toLowerCase().includes(query) ||
        t.shortDescription.toLowerCase().includes(query) ||
        t.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  }

  // Destinations filter
  if (filter.destinations && filter.destinations.length > 0) {
    filtered = filtered.filter((t) =>
      filter.destinations!.some((dest) =>
        t.destination.toLowerCase().includes(dest.toLowerCase())
      )
    );
  }

  // Duration filter
  if (filter.minDuration !== undefined) {
    filtered = filtered.filter((t) => t.duration >= filter.minDuration!);
  }
  if (filter.maxDuration !== undefined) {
    filtered = filtered.filter((t) => t.duration <= filter.maxDuration!);
  }

  // Budget filter
  if (filter.minBudget !== undefined) {
    filtered = filtered.filter((t) => t.estimatedBudget.max >= filter.minBudget!);
  }
  if (filter.maxBudget !== undefined) {
    filtered = filtered.filter((t) => t.estimatedBudget.min <= filter.maxBudget!);
  }

  // Difficulty filter
  if (filter.difficulty && filter.difficulty.length > 0) {
    filtered = filtered.filter((t) => filter.difficulty!.includes(t.difficulty));
  }

  // Travel style filter
  if (filter.travelStyle && filter.travelStyle.length > 0) {
    filtered = filtered.filter((t) =>
      t.travelStyle.some((style) => filter.travelStyle!.includes(style))
    );
  }

  // Tags filter (case-insensitive)
  if (filter.tags && filter.tags.length > 0) {
    const normalizedFilterTags = filter.tags.map((tag) => tag.toLowerCase());
    filtered = filtered.filter((t) =>
      t.tags.some((tag) => normalizedFilterTags.includes(tag.toLowerCase()))
    );
  }

  // Featured only
  if (filter.featuredOnly) {
    filtered = filtered.filter((t) => t.featured);
  }

  // NEW: Filter by creator
  if (filter.creatorId) {
    filtered = filtered.filter((t) => t.creatorId === filter.creatorId);
  }

  // NEW: Filter by status (only show approved for non-creators by default)
  if (filter.status && filter.status.length > 0) {
    filtered = filtered.filter((t) => filter.status!.includes(resolveMarketplaceTemplateStatus(t)));
  } else if (!filter.includeDrafts) {
    // Default: marketplace shows only live (approved) templates
    filtered = filtered.filter(
      (t) => resolveMarketplaceTemplateStatus(t) === 'approved',
    );
  }

  // NEW: Filter by verified creators only
  if (filter.verifiedOnly) {
    filtered = filtered.filter((t) => t.creatorVerified === true);
  }

  return filtered;
};

/**
 * Sort templates based on sort option
 */
export const sortTemplates = (
  templates: TripTemplate[],
  sortBy: TemplateSortBy
): TripTemplate[] => {
  const sorted = [...templates];

  switch (sortBy) {
    case 'popular':
      return sorted.sort((a, b) => b.usageCount - a.usageCount);
    case 'newest':
      return sorted.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    case 'budget-low':
      return sorted.sort((a, b) => a.estimatedBudget.min - b.estimatedBudget.min);
    case 'budget-high':
      return sorted.sort((a, b) => b.estimatedBudget.max - a.estimatedBudget.max);
    case 'duration-short':
      return sorted.sort((a, b) => a.duration - b.duration);
    case 'duration-long':
      return sorted.sort((a, b) => b.duration - a.duration);
    case 'rating':
      return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    default:
      return sorted;
  }
};

/**
 * Cache templates in AsyncStorage
 */
const cacheTemplates = async (
  templates: TripTemplate[],
  versionOverride?: string | null,
): Promise<void> => {
  try {
    const cacheData = JSON.stringify(dedupeTemplates(templates));
    const expiry = Date.now() + CACHE_DURATION_MS;
    const serverVersion = versionOverride ?? (await fetchTemplateVersionToken());

    await AsyncStorage.multiSet([
      [CACHE_KEY, cacheData],
      [CACHE_EXPIRY_KEY, expiry.toString()],
      [CACHE_VERSION_KEY, serverVersion || String(Date.now())],
    ]);

    console.log('[TripTemplates] Templates cached successfully');
  } catch (error) {
    console.error('[TripTemplates] Error caching templates:', error);
  }
};

/**
 * Backfill missing cover images using Unsplash landmark helper and persist to Firestore.
 * Returns updated templates array with any newly fetched images attached.
 */
const backfillTemplateImages = async (
  templates: TripTemplate[]
): Promise<TripTemplate[]> => {
  const FORCE_CURATED_SLUGS = new Set([
    'australia',
    'orlando',
    'mexico',
    'mexico-city',
    'hong-kong',
    'chicago',
    'new-york-city',
    'quebec-city',
    'morocco',
    'north-dakota',
    'tanzania',
    'new-zealand',
    'greenland',
    'philippines',
    'vietnam',
    'egypt',
    'spain',
    'peru',
    'italy',
    'costa-rica',
    'mauritius',
    'deer-valley-park-city',
    'taiwan',
    'morocco',
    'mexico-city',
    'hong-kong',
    'greenland',
    'chicago',
  ]);

  const results = await Promise.all(
    templates.map(async (template) => {
      const templateWithArrival = ensureArrivalCheckIn(template);

      const destinationSlug = getDestinationSlug(templateWithArrival.destination || templateWithArrival.title || '');
      const curated = destinationSlug ? getCuratedDestinationImage(destinationSlug) : null;

      // If we already have a cover image and it's not a generic Unsplash Source fallback, keep it
      const hasCover = !!templateWithArrival.coverImage;
    const isGenericSource =
      hasCover &&
      (templateWithArrival.coverImage?.includes('source.unsplash.com') ||
        templateWithArrival.coverImage?.includes('placehold.co'));

      // Prefer curated images when missing or when using generic source fallback; never overwrite a non-generic user-set cover
      if (curated && (!hasCover || isGenericSource)) {
        const updatedTemplate: TripTemplate = {
          ...templateWithArrival,
          coverImage: curated,
          coverImageCredit: templateWithArrival.coverImageCredit,
          coverImageCreditUrl: templateWithArrival.coverImageCreditUrl,
        };
        // Firestore rules allow only read on tripTemplates; writes are backend-only. Skip client persist.
        return updatedTemplate;
      }

      // If cover exists but is a placeholder/generic fallback, treat as missing so we can re-fetch.
      if (hasCover && !isGenericSource) {
        return templateWithArrival;
      }

      try {
        const imageResult = await fetchAndCacheLandmarkImage(template.destination, { forceRefresh: false });
        if (!imageResult?.url || imageResult.source === 'generic') {
          return templateWithArrival;
        }

        const updatedTemplate: TripTemplate = {
          ...templateWithArrival,
          coverImage: imageResult.url,
          coverImageCredit: imageResult.creditText ?? template.coverImageCredit,
          coverImageCreditUrl: imageResult.creditUrl ?? template.coverImageCreditUrl,
        };
        // Firestore rules allow only read on tripTemplates; writes are backend-only. Skip client persist.
        return updatedTemplate;
      } catch (error) {
        console.warn('[TripTemplates] Backfill image failed for', template.destination, error);
        return templateWithArrival;
      }
    })
  );

  return results;
};

/**
 * Ensure day 1 starts with a hotel check-in to align with legacy template format.
 */
const ensureArrivalCheckIn = (template: TripTemplate): TripTemplate => {
  if (!template.itinerary || template.itinerary.length === 0) {
    return template;
  }

  const firstDay = template.itinerary[0];
  if (!firstDay.activities || firstDay.activities.length === 0) {
    return template;
  }

  const firstActivity = firstDay.activities[0];
  const hasCheckIn =
    firstActivity.category === 'lodging' ||
    firstActivity.category === 'hotel' ||
    /check[-\s]?in/i.test(firstActivity.title);

  if (hasCheckIn) {
    return template;
  }

  const checkInActivity = {
    time: '15:00',
    title: 'Hotel Check-in',
    category: 'lodging' as const,
    location: template.destination,
    description: 'Settle into your stay before exploring',
    estimatedCost: 0,
    duration: 60,
  };

  const updatedFirstDay = {
    ...firstDay,
    activities: [checkInActivity, ...firstDay.activities],
  };

  const updatedItinerary = [updatedFirstDay, ...template.itinerary.slice(1)];

  return {
    ...template,
    itinerary: updatedItinerary,
  };
};

/**
 * Get cached templates if not expired
 */
const getCachedTemplates = async (expectedVersion?: string | null): Promise<TripTemplate[] | null> => {
  try {
    const [[, cacheData], [, expiryStr], [, cachedVersion]] = await AsyncStorage.multiGet([
      CACHE_KEY,
      CACHE_EXPIRY_KEY,
      CACHE_VERSION_KEY,
    ]);

    if (!cacheData || !expiryStr) {
      return null;
    }

    if (expectedVersion && cachedVersion && expectedVersion !== cachedVersion) {
      logTripTemplatesVerbose('[TripTemplates] Cache version mismatch, invalidating');
      await clearTemplatesCache();
      return null;
    }

    const expiry = parseInt(expiryStr, 10);
    if (Date.now() > expiry) {
      console.log('[TripTemplates] Cache expired');
      await clearTemplatesCache();
      return null;
    }

    const templates = JSON.parse(cacheData);
    
    // Convert date strings back to Date objects
    return dedupeTemplates(
      templates.map((t: any) => ({
      ...t,
      createdAt: new Date(t.createdAt),
      updatedAt: new Date(t.updatedAt),
      destinations: t.destinations?.map((d: any) => ({
        ...d,
        startDate: new Date(d.startDate),
        endDate: new Date(d.endDate),
      })),
      })),
    );
  } catch (error) {
    console.error('[TripTemplates] Error reading cache:', error);
    return null;
  }
};

/**
 * Clear templates cache
 */
export const clearTemplatesCache = async (): Promise<void> => {
  try {
    await AsyncStorage.multiRemove([CACHE_KEY, CACHE_EXPIRY_KEY, CACHE_VERSION_KEY]);
    console.log('[TripTemplates] Cache cleared');
  } catch (error) {
    console.error('[TripTemplates] Error clearing cache:', error);
  }
};

/**
 * Get featured templates only
 */
export const getFeaturedTemplates = async (): Promise<TripTemplate[]> => {
  const templates = await fetchTripTemplates();
  return templates.filter((t) => t.featured);
};

/**
 * Search templates by query
 */
export const searchTemplates = async (query: string): Promise<TripTemplate[]> => {
  const templates = await fetchTripTemplates();
  return filterTemplates(templates, { searchQuery: query });
};

/**
 * Convert an existing trip to a template
 * This allows users to share their actual trips as templates
 */
export const convertTripToTemplate = async (
  tripId: string,
  userId: string,
  userDisplayName: string,
  userAvatar: string | undefined,
  options?: {
    includePersonalInfo?: boolean; // Include personal notes, etc.
    anonymize?: boolean; // Remove personal details
  }
): Promise<string> => {
  try {
    console.log('[TripTemplates] Converting trip to template');

    // Fetch trip data
    const tripRef = doc(db, 'trips', tripId);
    const tripSnap = await getDoc(tripRef);
    if (!tripSnap.exists()) {
      throw new Error('Trip not found');
    }
    const tripData = tripSnap.data() as any;
    const trip = {
      id: tripId,
      owner: tripData.owner,
      name: tripData.title ?? tripData.name ?? '',
      destination: tripData.destination,
      startDate: tripData.startDate instanceof Timestamp ? tripData.startDate.toDate() : new Date(tripData.startDate),
      endDate: tripData.endDate instanceof Timestamp ? tripData.endDate.toDate() : new Date(tripData.endDate),
      budget: typeof tripData.budget === 'number' ? tripData.budget : null,
      baseCurrency: tripData.baseCurrency,
      landmarkImage: tripData.landmarkImage,
      landmarkImageCredit: tripData.landmarkImageCredit,
      landmarkImageCreditUrl: tripData.landmarkImageCreditUrl,
      collaborators: Array.isArray(tripData.collaborators) ? tripData.collaborators : [],
    };

    // Verify user owns the trip
    if (trip.owner !== userId) {
      throw new Error('You can only convert your own trips to templates');
    }

    // Fetch itinerary items
    const { fetchTripItineraryItems } = await import('./itinerary');
    const itineraryItems = await fetchTripItineraryItems(tripId);

    // Calculate duration
    const duration = Math.ceil(
      (trip.endDate.getTime() - trip.startDate.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

    // Group itinerary items by day
    const daysByDate = new Map<string, any[]>();
    
    itineraryItems.forEach((item) => {
      const itemDate = new Date(item.startDate);
      itemDate.setHours(0, 0, 0, 0);
      const dateKey = itemDate.toISOString().split('T')[0];
      
      if (!daysByDate.has(dateKey)) {
        daysByDate.set(dateKey, []);
      }
      daysByDate.get(dateKey)!.push(item);
    });

    // Convert to template days format
    const templateDays: any[] = [];
    const sortedDates = Array.from(daysByDate.keys()).sort();
    
    sortedDates.forEach((dateKey, index) => {
      const items = daysByDate.get(dateKey)!;
      const dayDate = new Date(dateKey);
      
      // Sort items by start time
      const sortedItems = items.sort((a, b) => {
        const timeA = a.startTime || '00:00';
        const timeB = b.startTime || '00:00';
        return timeA.localeCompare(timeB);
      });

      // Convert items to template activities
      const activities = sortedItems.map((item) => {
        // Map itinerary category to template category
        const categoryMap: Record<string, string> = {
          hotel: 'lodging',
          flight: 'flight',
          activity: 'activity',
          restaurant: 'restaurant',
          other: 'other',
        };

        return {
          time: item.startTime || '09:00',
          title: item.title,
          category: categoryMap[item.category] || 'activity',
          description: options?.includePersonalInfo ? item.description : undefined,
          location: item.location,
          address: item.address,
          duration: item.endTime && item.startTime
            ? calculateDurationMinutes(item.startTime, item.endTime)
            : undefined,
        };
      });

      templateDays.push({
        day: index + 1,
        title: `Day ${index + 1}: ${getDayTitle(items)}`,
        description: options?.includePersonalInfo
          ? `Activities for day ${index + 1}`
          : undefined,
        activities,
      });
    });

    // Build template data
    const templateData: Omit<
      TripTemplate,
      'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'status' | 'creatorId' | 'creatorName' | 'creatorAvatar' | 'createdBy'
    > = {
      title: `${trip.name} - ${trip.destination || 'Trip Template'}`,
      destination: trip.destination || 'Unknown Destination',
      duration,
      shortDescription: `A ${duration}-day trip to ${trip.destination || 'your destination'}. Created from an actual trip experience.`,
      longDescription: options?.includePersonalInfo
        ? `This template is based on an actual trip taken by ${userDisplayName}.`
        : undefined,
      coverImage: trip.landmarkImage,
      coverImageCredit: trip.landmarkImageCredit || undefined,
      coverImageCreditUrl: trip.landmarkImageCreditUrl || undefined,
      difficulty: 'moderate', // Default, user can adjust
      travelStyle: inferTravelStyles(itineraryItems),
      featured: false,
      estimatedBudget: {
        min: trip.budget ? Math.round(trip.budget * 0.8) : 0,
        max: trip.budget ? Math.round(trip.budget * 1.2) : 0,
        currency: trip.baseCurrency || 'USD',
        breakdown: {
          flights: 0, // Would need expense data to calculate
          lodging: 0,
          food: 0,
          activities: 0,
          transport: 0,
        },
      },
      itinerary: templateDays,
      bestMonths: inferBestMonths(trip.startDate),
      tags: inferTags(itineraryItems, trip),
      highlights: extractHighlights(itineraryItems),
    };

    // Create the template
    const templateId = await createUserTemplate(
      userId,
      userDisplayName,
      userAvatar,
      templateData
    );

    console.log('[TripTemplates] Successfully converted trip to template:', templateId);
    return templateId;
  } catch (error) {
    console.error('[TripTemplates] Error converting trip to template:', error);
    throw error;
  }
};

/**
 * Helper: Calculate duration in minutes from time strings
 */
const calculateDurationMinutes = (startTime: string, endTime: string): number => {
  const [startHours, startMins] = startTime.split(':').map(Number);
  const [endHours, endMins] = endTime.split(':').map(Number);
  
  const startTotal = startHours * 60 + startMins;
  const endTotal = endHours * 60 + endMins;
  
  return endTotal - startTotal;
};

/**
 * Helper: Generate a day title from activities
 */
const getDayTitle = (items: any[]): string => {
  if (items.length === 0) return 'Free Day';
  if (items.length === 1) return items[0].title;
  
  // Use first significant activity
  const significant = items.find((item) => 
    item.category !== 'other' && item.category !== 'transport'
  );
  
  return significant?.title || items[0].title;
};

/**
 * Helper: Infer travel styles from itinerary
 */
const inferTravelStyles = (items: any[]): TravelStyle[] => {
  const styles: TravelStyle[] = [];
  const categories = new Set(items.map((item) => item.category));
  
  if (categories.has('restaurant')) styles.push('food');
  if (categories.has('activity')) styles.push('adventure');
  if (categories.has('hotel')) styles.push('luxury');
  
  // Default to cultural if no specific styles detected
  if (styles.length === 0) styles.push('cultural');
  
  return styles;
};

/**
 * Helper: Infer best months from trip date
 */
const inferBestMonths = (startDate: Date): string[] => {
  const month = startDate.getMonth();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Include the month and adjacent months
  const months: string[] = [];
  for (let i = -1; i <= 1; i++) {
    const idx = (month + i + 12) % 12;
    months.push(monthNames[idx]);
  }
  
  return months;
};

/**
 * Helper: Infer tags from trip and itinerary
 */
const inferTags = (items: any[], trip: any): string[] => {
  const tags: string[] = [];
  
  // Solo-friendly if single traveler
  if (!trip.collaborators || trip.collaborators.length === 0) {
    tags.push('solo-friendly');
  }
  
  // Budget-friendly if budget is reasonable
  if (trip.budget && trip.budget < 2000) {
    tags.push('budget');
  }
  
  return tags;
};

/**
 * Helper: Extract highlights from itinerary
 */
const extractHighlights = (items: any[]): string[] => {
  // Get top activities (non-hotel, non-flight)
  const highlights = items
    .filter((item) => item.category !== 'hotel' && item.category !== 'flight')
    .slice(0, 5)
    .map((item) => item.title);
  
  return highlights;
};

/**
 * Get templates by creator
 */
export const getTemplatesByCreator = async (
  creatorId: string,
  includeDrafts: boolean = false
): Promise<TripTemplate[]> => {
  try {
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const constraints = [
      where('creatorId', '==', creatorId),
    ];

    // If not including drafts, only get approved templates
    if (!includeDrafts) {
      constraints.push(where('status', '==', 'approved'));
    }

    const templatesQuery = query(
      templatesRef,
      ...constraints,
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(templatesQuery);
    const templates: TripTemplate[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as FirestoreTripTemplate;
      templates.push(convertFirestoreTemplate(doc.id, data));
    });

    return templates;
  } catch (error) {
    console.error('[TripTemplates] Error fetching templates by creator:', error);
    throw error;
  }
};

/**
 * Fetch templates with status 'pending' for admin moderation backlog.
 * Call from admin UI only; any signed-in user can run the query (Firestore read is allowed).
 */
export const fetchPendingTemplates = async (): Promise<TripTemplate[]> => {
  try {
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const pendingQuery = query(
      templatesRef,
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(pendingQuery);
    const templates: TripTemplate[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as FirestoreTripTemplate;
      templates.push(convertFirestoreTemplate(docSnap.id, data));
    });
    return templates;
  } catch (error) {
    console.error('[TripTemplates] Error fetching pending templates:', error);
    throw error;
  }
};

/**
 * Create a user-generated template
 * Validates the template and saves it with 'pending' status for moderation
 */
export const createUserTemplate = async (
  userId: string,
  userDisplayName: string,
  userAvatar: string | undefined,
  templateData: Omit<
    TripTemplate,
    'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'status' | 'creatorId' | 'creatorName' | 'creatorAvatar' | 'createdBy'
  >
): Promise<string> => {
  try {
    console.log('[TripTemplates] Creating user template:', templateData.title);

    // Validate template
    const validationErrors = validateTemplate(templateData);
    if (validationErrors.length > 0) {
      throw new Error(`Template validation failed: ${validationErrors.join(', ')}`);
    }

    // Prepare Firestore document
    const now = Timestamp.now();
    const firestoreTemplate: Omit<FirestoreTripTemplate, 'id'> = {
      ...templateData,
      destinations: templateData.destinations?.map((d) => ({
        ...d,
        startDate: Timestamp.fromDate(d.startDate),
        endDate: Timestamp.fromDate(d.endDate),
      })),
      status: 'pending' as TemplateStatus,
      creatorId: userId,
      creatorName: userDisplayName,
      creatorAvatar: userAvatar,
      creatorVerified: false, // Will be set by admin
      createdBy: 'community',
      usageCount: 0,
      createdAt: now,
      updatedAt: now,
    };

    // Save to Firestore
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const docRef = await addDoc(templatesRef, firestoreTemplate);

    console.log('[TripTemplates] User template created:', docRef.id);

    // Track template creation
    try {
      const { logAnalyticsEvent } = await import('./analyticsLogger');
      await logAnalyticsEvent('template_created' as any, {
        templateId: docRef.id,
        creatorId: userId,
        destination: templateData.destination,
        duration: templateData.duration,
      });
    } catch (error) {
      console.warn('[TripTemplates] Failed to track template creation:', error);
    }

    // Clear cache to include new template
    await clearTemplatesCache();

    return docRef.id;
  } catch (error) {
    console.error('[TripTemplates] Error creating user template:', error);
    throw error;
  }
};

/**
 * Validate template data before creation
 */
const validateTemplate = (
  template: Omit<
    TripTemplate,
    'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'status' | 'creatorId' | 'creatorName' | 'creatorAvatar' | 'createdBy'
  >
): string[] => {
  const errors: string[] = [];

  // Basic fields
  if (!template.title || template.title.trim().length < 5) {
    errors.push('Title must be at least 5 characters');
  }

  if (!template.destination || template.destination.trim().length < 3) {
    errors.push('Destination is required');
  }

  if (!template.shortDescription || template.shortDescription.trim().length < 50) {
    errors.push('Short description must be at least 50 characters');
  }

  // Duration
  if (template.duration < 1 || template.duration > 365) {
    errors.push('Duration must be between 1 and 365 days');
  }

  // Itinerary
  if (!template.itinerary || template.itinerary.length < MIN_TEMPLATE_ITINERARY_DAYS) {
    errors.push(
      MIN_TEMPLATE_ITINERARY_DAYS === 1
        ? 'Template must have at least 1 day of itinerary'
        : `Template must have at least ${MIN_TEMPLATE_ITINERARY_DAYS} days of itinerary`,
    );
  }

  // Validate each day
  template.itinerary?.forEach((day, dayIndex) => {
    if (!day.title || day.title.trim().length === 0) {
      errors.push(`Day ${dayIndex + 1} must have a title`);
    }

    if (!day.activities || day.activities.length === 0) {
      errors.push(`Day ${dayIndex + 1} must have at least one activity`);
    }

    day.activities?.forEach((activity, activityIndex) => {
      if (!activity.title || activity.title.trim().length === 0) {
        errors.push(`Day ${dayIndex + 1}, Activity ${activityIndex + 1} must have a title`);
      }

      if (!activity.time || !/^\d{2}:\d{2}$/.test(activity.time)) {
        errors.push(`Day ${dayIndex + 1}, Activity ${activityIndex + 1} must have a valid time (HH:mm)`);
      }
    });
  });

  // Budget
  if (!template.estimatedBudget) {
    errors.push('Estimated budget is required');
  } else {
    if (template.estimatedBudget.min < 0 || template.estimatedBudget.max < 0) {
      errors.push('Budget values must be positive');
    }

    if (template.estimatedBudget.min > template.estimatedBudget.max) {
      errors.push('Minimum budget cannot be greater than maximum budget');
    }

    if (!template.estimatedBudget.currency || template.estimatedBudget.currency.length !== 3) {
      errors.push('Currency must be a valid 3-letter ISO code');
    }
  }

  // Travel style
  if (!template.travelStyle || template.travelStyle.length === 0) {
    errors.push('At least one travel style must be selected');
  }

  return errors;
};

